/**
 * マッチング結果一覧 API hook
 * /matching/results 画面で使用するフィルタ・ページネーション付き一覧取得
 */
import { useCallback, useEffect, useState } from "react";
import { restClient } from "../lib/rest-client";

// =====================================
// Types
// =====================================

export type MatchingResultFeedback = {
  id: string;
  rating: number;
  comment: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export type MatchingResultListItem = {
  id: string;
  triggerType: string;
  sourceEmailId: string;
  sourceType: string;
  sourceSubject: string | null;
  matchedEmailId: string | null;
  matchedSubject: string;
  matchedPresetId: string | null;
  sourceSnapshot: Record<string, unknown> | null;
  matchedSnapshot: Record<string, unknown> | null;
  totalScore: number;
  skillScore: number | null;
  rewardScore: number | null;
  locationScore: number | null;
  availabilityScore: number | null;
  depthScore: number | null;
  aiReasoning: string | null;
  aiHighlights: string[] | null;
  aiRisks: string[] | null;
  status: string;
  actedBy: string | null;
  actedAt: string | null;
  feedback: MatchingResultFeedback | null;
  createdAt: string;
};

type MatchingResultListResponse = {
  items: MatchingResultListItem[];
  total: number;
  page: number;
  limit: number;
};

export type MatchingResultFilters = {
  triggerTypes: string[];
  sourceTypes: string[];
  statuses: string[];
};

export type MatchingResultSort = {
  field: "created_at" | "total_score";
  order: "asc" | "desc";
};

type UseMatchingResultsReturn = {
  results: MatchingResultListItem[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  error: Error | undefined;
  filters: MatchingResultFilters;
  sort: MatchingResultSort;
  setPage: (page: number) => void;
  setFilters: (filters: MatchingResultFilters) => void;
  setSort: (sort: MatchingResultSort) => void;
  refetch: () => void;
};

/**
 * マッチング結果一覧を取得する hook
 * @param {number} initialLimit 1ページあたりの件数
 * @return {UseMatchingResultsReturn} results, total, page, filters, sort 等
 */
export function useMatchingResults(
  initialLimit = 20,
): UseMatchingResultsReturn {
  const [results, setResults] = useState<MatchingResultListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(initialLimit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();
  const [filters, setFiltersState] = useState<MatchingResultFilters>({
    triggerTypes: [],
    sourceTypes: [],
    statuses: [],
  });
  const [sort, setSortState] = useState<MatchingResultSort>({
    field: "created_at",
    order: "desc",
  });

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("sort", sort.field);
      params.set("order", sort.order);
      if (filters.triggerTypes.length > 0) {
        params.set("trigger_type", filters.triggerTypes.join(","));
      }
      if (filters.sourceTypes.length > 0) {
        params.set("source_type", filters.sourceTypes.join(","));
      }
      if (filters.statuses.length > 0) {
        params.set("status", filters.statuses.join(","));
      }

      const data = await restClient.get<MatchingResultListResponse>(
        `/matching/results?${params.toString()}`,
      );
      setResults(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters, sort]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const setFilters = useCallback((newFilters: MatchingResultFilters) => {
    setFiltersState(newFilters);
    setPage(1);
  }, []);

  const setSort = useCallback((newSort: MatchingResultSort) => {
    setSortState(newSort);
    setPage(1);
  }, []);

  return {
    results,
    total,
    page,
    limit,
    loading,
    error,
    filters,
    sort,
    setPage,
    setFilters,
    setSort,
    refetch: fetchResults,
  };
}
